import type { ShareCardConfig, ShareCardPattern } from '@tabbio/types';
import {
  SHARE_CARD_CTA_MAX,
  SHARE_CARD_DEFAULT_CTA,
  SHARE_CARD_DEFAULT_CUSTOM_COLOR,
  SHARE_CARD_DESC_MAX,
  SHARE_CARD_DOWNLOAD_CTA,
  SHARE_CARD_PATTERNS,
  SHARE_CARD_THEME_IDS,
  SHARE_CARD_THEMES,
  SHARE_CARD_TITLE_MAX,
  buildCustomBackground,
  customBackgroundHex,
  defaultShareCardConfig,
  normalizeShareCardPattern,
  paidShareCardChoices,
  parseShareCardUrl,
  sanitizeShareCardForFree,
  shareCardCtaPreset,
  type ShareCardCtaPreset,
  type ShareCardPaidChoice,
  type ShareCardThemeId,
} from '@tabbio/utils/cv-share-card';
import {
  SHARE_CARD_BRIEFCASE_ART,
  SHARE_CARD_CV_ART,
  SHARE_CARD_MARK_ART,
  SHARE_CARD_WAVE_ART,
  type ShareCardSvgArt,
} from '@tabbio/utils/share-card-art';

/**
 * Pure rules behind the "Edit share preview" editor, shared by the native
 * sheet and the web dialog so both gate, parse and save identically (and so
 * the behaviour is testable without rendering). The entitlement matrix
 * itself lives in @tabbio/utils (paidShareCardChoices / the Free sanitizer);
 * this module only turns it into editor decisions.
 */

/** Picker order (design: white, black, tabbio, custom, then the gradients). */
export const SHARE_CARD_EDITOR_THEME_IDS: readonly ShareCardThemeId[] = SHARE_CARD_THEME_IDS;
export const SHARE_CARD_EDITOR_PATTERNS: readonly ShareCardPattern[] = SHARE_CARD_PATTERNS;

/** The editor caps the headline at the design's 100 (the model allows 110). */
export const SHARE_CARD_HEADLINE_INPUT_MAX = Math.min(100, SHARE_CARD_DESC_MAX);
export const SHARE_CARD_NAME_INPUT_MAX = SHARE_CARD_TITLE_MAX;
export const SHARE_CARD_CTA_INPUT_MAX = SHARE_CARD_CTA_MAX;

/** The two free CTA presets, in chip order. */
export const SHARE_CARD_CTA_PRESETS: readonly { id: ShareCardCtaPreset; label: string }[] = [
  { id: 'view', label: SHARE_CARD_DEFAULT_CTA },
  { id: 'download', label: SHARE_CARD_DOWNLOAD_CTA },
];

/** A copy of the card without one optional field (absence is how defaults are stored). */
function without(card: ShareCardConfig, key: 'pattern' | 'cta' | 'cardUrl'): ShareCardConfig {
  const copy: ShareCardConfig = { ...card };
  delete copy[key];
  return copy;
}

export function isPaidShareCardTheme(id: ShareCardThemeId): boolean {
  return SHARE_CARD_THEMES.find((theme) => theme.id === id)?.pro ?? true;
}

/** The card the editor starts from: the saved card, or the untouched default. */
export function initialShareCardDraft(saved: ShareCardConfig | null | undefined): ShareCardConfig {
  return saved ?? defaultShareCardConfig({ showPhoto: true });
}

// ─── Save gating ─────────────────────────────────────────────

export type ShareCardSaveMode = 'save' | 'upgrade';

export interface ShareCardSaveGate {
  /** `upgrade` when a Free user holds paid choices; `save` otherwise. */
  mode: ShareCardSaveMode;
  /** The paid choices in the draft (empty for Premium users' purposes too, but listed). */
  paid: ShareCardPaidChoice[];
  /** Offer "Restore free options": a Free user with paid choices. */
  canRestoreFree: boolean;
}

/**
 * Preview-first: anyone may try anything, but a Free draft that carries paid
 * choices is saved only after upgrading. Premium saves as is; the server
 * re-applies the same sanitizer if the entitlement lapsed.
 */
export function shareCardSaveGate(card: ShareCardConfig, canBrand: boolean): ShareCardSaveGate {
  const paid = paidShareCardChoices(card);
  const blocked = !canBrand && paid.length > 0;
  return { mode: blocked ? 'upgrade' : 'save', paid, canRestoreFree: blocked };
}

/** "Restore free options": the Free version of the draft, with name/headline/photo kept. */
export function restoreFreeShareCard(card: ShareCardConfig): ShareCardConfig {
  return sanitizeShareCardForFree(card);
}

// ─── Theme + custom colour ───────────────────────────────────

/** The hex the custom swatch shows: the draft's colour, else the design default. */
export function shareCardCustomHex(card: ShareCardConfig, lastHex?: string | null): string {
  return customBackgroundHex(card.background) ?? lastHex ?? SHARE_CARD_DEFAULT_CUSTOM_COLOR;
}

/**
 * Pick a theme tile. `custom` resolves to the last custom colour (or the
 * default violet). A theme replaces any Pro photo background, which would
 * otherwise hide the chosen theme.
 */
export function selectShareCardTheme(
  card: ShareCardConfig,
  id: ShareCardThemeId,
  lastHex?: string | null,
): ShareCardConfig {
  const background = id === 'custom' ? buildCustomBackground(shareCardCustomHex(card, lastHex)) : id;
  return { ...card, background, backgroundImage: null };
}

/**
 * A typed hex colour: 6 hex digits with or without `#`. Anything partial or
 * invalid returns null and the caller keeps the last valid colour (design:
 * "invalid input does not update the preview").
 */
export function parseShareCardHexInput(text: string): string | null {
  const match = text.trim().match(/^#?([0-9a-fA-F]{6})$/);
  return match ? `#${match[1].toLowerCase()}` : null;
}

export function applyShareCardCustomColor(card: ShareCardConfig, hex: string): ShareCardConfig {
  const parsed = parseShareCardHexInput(hex);
  if (!parsed) return card;
  return { ...card, background: buildCustomBackground(parsed), backgroundImage: null };
}

export function selectShareCardPattern(card: ShareCardConfig, pattern: ShareCardPattern): ShareCardConfig {
  const next = normalizeShareCardPattern(pattern);
  const rest = without(card, 'pattern');
  // The default silhouette is stored as absence, like the normaliser does.
  return next === 'signature' ? rest : { ...rest, pattern: next };
}

// ─── Text fields ─────────────────────────────────────────────

/** Name/headline inputs: empty means "use the profile value" (null). */
export function applyShareCardTitle(card: ShareCardConfig, text: string): ShareCardConfig {
  const value = text.slice(0, SHARE_CARD_NAME_INPUT_MAX);
  return { ...card, title: value.trim().length ? value : null };
}

export function applyShareCardHeadline(card: ShareCardConfig, text: string): ShareCardConfig {
  const value = text.slice(0, SHARE_CARD_HEADLINE_INPUT_MAX);
  return { ...card, description: value.trim().length ? value : null };
}

// ─── Call to action ──────────────────────────────────────────

/** What the "Button text" input shows: the stored wording, or the default preset. */
export function shareCardCtaFieldValue(card: ShareCardConfig): string {
  return card.cta?.length ? card.cta : SHARE_CARD_DEFAULT_CTA;
}

/**
 * Typed button wording. Empty falls back to the default preset (stored as
 * absence); the default preset itself is also stored as absence so a card
 * that only ever used "View CV" stays minimal.
 */
export function applyShareCardCta(card: ShareCardConfig, text: string): ShareCardConfig {
  const value = text.slice(0, SHARE_CARD_CTA_INPUT_MAX);
  const rest = without(card, 'cta');
  if (!value.trim().length || value.trim() === SHARE_CARD_DEFAULT_CTA) return rest;
  return { ...rest, cta: value };
}

/** The preset chip that matches the draft's wording, if any. */
export function shareCardSelectedCtaPreset(card: ShareCardConfig): ShareCardCtaPreset | null {
  return shareCardCtaPreset(card.cta);
}

// ─── Card link ───────────────────────────────────────────────

/** What the "Card link" input shows: the custom link, nothing when cleared, else the default. */
export function shareCardLinkFieldValue(card: ShareCardConfig, defaultUrl: string | null | undefined): string {
  if (card.cardUrl === undefined) return defaultUrl ?? '';
  return card.cardUrl ?? '';
}

/** "Reset to default" shows whenever the link differs from the profile link, cleared included. */
export function isShareCardLinkChanged(card: ShareCardConfig): boolean {
  return card.cardUrl !== undefined;
}

/**
 * Typed card link. Empty clears the row (`null`); the profile link itself (or
 * its display form) returns to the default (`undefined`); any other valid
 * http(s) URL is kept; an invalid or partial URL hides the row while typing,
 * exactly like the design's `shareLink` does.
 */
export function applyShareCardLinkInput(
  card: ShareCardConfig,
  text: string,
  defaultUrl: string | null | undefined,
): ShareCardConfig {
  const rest = without(card, 'cardUrl');
  const raw = text.trim();
  if (!raw) return { ...rest, cardUrl: null };
  // A bare host ("ahmed.tabbio.com") is read as https; an explicit scheme is kept as typed.
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw);
  const parsed = parseShareCardUrl(hasScheme ? raw : `https://${raw}`);
  if (!parsed) return { ...rest, cardUrl: null };
  const fallback = defaultUrl ? parseShareCardUrl(defaultUrl) : null;
  if (fallback && parsed === fallback) return rest;
  return { ...rest, cardUrl: parsed };
}

export function resetShareCardLink(card: ShareCardConfig): ShareCardConfig {
  return without(card, 'cardUrl');
}

export function clearShareCardLink(card: ShareCardConfig): ShareCardConfig {
  return { ...card, cardUrl: null };
}

// ─── Picker swatches ─────────────────────────────────────────

/** The miniature each silhouette tile shows (design: `.pattern-swatch`). */
export const SHARE_CARD_PATTERN_SWATCH_ART: Record<
  Exclude<ShareCardPattern, 'none' | 'orbit'>,
  { art: ShareCardSvgArt; size: number }
> = {
  signature: { art: SHARE_CARD_MARK_ART, size: 16 },
  cv: { art: SHARE_CARD_CV_ART, size: 14 },
  arch: { art: SHARE_CARD_BRIEFCASE_ART, size: 20 },
  wave: { art: SHARE_CARD_WAVE_ART, size: 20 },
};

/** The small colour disc on a theme tile, where no rendered art stands in. */
export function shareCardThemeSwatchCss(id: ShareCardThemeId): string {
  if (id === 'custom') return 'conic-gradient(#f75, #fa5, #7c9, #76f, #f75)';
  return SHARE_CARD_THEMES.find((theme) => theme.id === id)?.css ?? '#ffffff';
}
